/**
 * DEV-ONLY user switcher.
 * URL : /dev/switch-user
 *
 * Affiche les 15 comptes APOLLO seedés et permet un login direct
 * via POST /api/auth/dev-login (qui est lui-même gated sur APP_ENV != prod).
 *
 * Si le backend tourne en prod, l'appel renverra 404 et la page affichera
 * un message d'erreur clair. La page elle-même n'est PAS gated côté frontend
 * (inutile : aucune information sensible exposée, et la sécurité est appliquée
 * sur l'endpoint backend).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Crown, ShieldCheck, MapPin, Briefcase, Building2,
  Loader2, Globe2, ArrowRight, FlaskConical, Trash2, AlertTriangle,
} from "lucide-react";
import { api } from "../lib/api";
import { useUser } from "../contexts/UserContext";
import { WordmarkInline } from "../components/Wordmark";

// 15 membres APOLLO seedés (cf. /app/backend/migrations/break_news_init.sql)
const ACCOUNTS = [
  // CEO / Direction
  { key: "cc90af7e-58ff-4d47-8e6c-5b577eedcfa9", name: "Gilles IACONES",
    role: "CEO", group: "VENUS · IDF", country: "FR", category: "direction",
    icon: Crown, color: "#F77F00", note: "Tous droits + collège direction" },
  { key: "726791c6-9944-4be1-a821-736e72bb3cff", name: "Bénédicte PELTIER",
    role: "Direction", group: "VENUS · IDF", country: "FR", category: "direction",
    icon: ShieldCheck, color: "#F77F00", note: "Collège direction · modération éditoriale" },
  // DN
  { key: "ce186988-9155-41df-aedb-5a454361ee37", name: "Robert FRENCH",
    role: "DN UK", group: "—", country: "GB", category: "dn",
    icon: Globe2, color: "#3B82F6", note: "Auto-approve ops · éditorial → collège" },
  { key: "68541874-49f7-4110-a1df-e5cc8447b0ee", name: "Mel FRENCH",
    role: "DN UK", group: "—", country: "GB", category: "dn",
    icon: Globe2, color: "#3B82F6", note: "Auto-approve ops · éditorial → collège" },
  // DR
  { key: "350118c1-1723-4c7c-86a9-cb5a169f03c4", name: "Dyane DEMOURGUES",
    role: "DR IDF", group: "Capucines · IDF", country: "FR", category: "dr",
    icon: MapPin, color: "#10B981", note: "Modère présidents VENUS (Arthur)" },
  { key: "56a4f866-eeee-4305-8e4f-885bf0e88458", name: "Catherine DOMMERGUE",
    role: "DR Normandie", group: "Caen Drakkars", country: "FR", category: "dr",
    icon: MapPin, color: "#10B981", note: "Modère Caen Drakkars (Guillaume, Françis)" },
  { key: "6692e210-a418-44e5-a6dc-81d54064237b", name: "Virginie PERROT",
    role: "DR ARA", group: "The First Clermont", country: "FR", category: "dr",
    icon: MapPin, color: "#10B981", note: "Modère Annecy / Clermont (Coralie)" },
  // Consultants
  { key: "677ea815-01e9-4611-9e0c-d8932732ce70", name: "Françis KIRSCHVING",
    role: "Consultant", group: "Caen Drakkars · Normandie", country: "FR",
    category: "consultant", icon: Briefcase, color: "#8B5CF6",
    note: "Quota 10/7j · ops → DR Catherine" },
  { key: "9cefad62-9c36-4318-a53b-3d1d85291de6", name: "Hugo SIGNORET",
    role: "Consultant", group: "(sans groupe)", country: "FR", category: "consultant",
    icon: Briefcase, color: "#8B5CF6",
    note: "Edge case : pas de région → escalade collège" },
  { key: "332204b6-531d-47dc-9bc7-dfa97b03bcfc", name: "Émilie MONTEIL",
    role: "Consultant", group: "(sans groupe)", country: "FR", category: "consultant",
    icon: Briefcase, color: "#8B5CF6", note: "Quota 10/7j" },
  { key: "b223aa92-d04d-4713-9a08-cd9efd5ea5e5", name: "Christian MAGNE",
    role: "Consultant", group: "Libourne · Nouvelle-Aquitaine", country: "FR",
    category: "consultant", icon: Briefcase, color: "#8B5CF6",
    note: "Quota 10/7j" },
  // Présidents
  { key: "cdad6f4a-e624-4ac6-8758-2c8e53333892", name: "Arthur LE GUILLOU",
    role: "Président", group: "VENUS · IDF", country: "FR", category: "president",
    icon: Building2, color: "#EC4899",
    note: "Quota 5/7j · ses posts vont vers Dyane (DR IDF)" },
  { key: "0ef66378-d47f-490f-95ad-e7ea37ebf525", name: "Guillaume DAUMER",
    role: "Président", group: "Caen Drakkars · Normandie", country: "FR",
    category: "president", icon: Building2, color: "#EC4899",
    note: "Quota 5/7j · va vers Catherine (DR Normandie)" },
  { key: "fd1be9fd-0f88-41b8-8a52-c71c15cb2a32", name: "Muriel GALLERAND",
    role: "Présidente", group: "Le Mans · Pays de la Loire", country: "FR",
    category: "president", icon: Building2, color: "#EC4899",
    note: "Edge case : Pays de la Loire sans DR seedé" },
  { key: "a26dbea1-4d77-49ed-9168-74cb02971cd8", name: "Coralie VASSEUR",
    role: "Présidente", group: "Annecy le Lac · ARA", country: "FR",
    category: "president", icon: Building2, color: "#EC4899",
    note: "Va vers Virginie (DR ARA)" },
];

const CATEGORY_ORDER = [
  ["direction", "Direction / CEO", "Auto-approve tout · accès collège"],
  ["dn", "Directeurs Nationaux", "Auto-approve ops · éditorial → collège"],
  ["dr", "Directeurs Régionaux", "Modèrent les présidents/consultants de leur région"],
  ["consultant", "Consultants", "Quota 10/7j · ops → DR, éditorial → collège"],
  ["president", "Présidents", "Quota 5/7j · tous leurs posts → DR du groupe"],
];

export default function DevSwitchUser() {
  const navigate = useNavigate();
  const { refresh } = useUser();
  const [loadingKey, setLoadingKey] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  // Gating prod : si le backend tourne en mode prod, on redirige
  // immédiatement vers le feed (ou login si non authentifié).
  useEffect(() => {
    api
      .get("/health")
      .then((r) => {
        if (r.data?.env === "prod") {
          navigate("/", { replace: true });
        }
      })
      .catch(() => {});
  }, [navigate]);

  const loginAs = async (account) => {
    setLoadingKey(account.key);
    try {
      await api.post("/auth/dev-login", { user_key: account.key });
      await refresh();
      toast.success(`Connecté en tant que ${account.name}`);
      navigate("/");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("dev-login désactivé : le backend est en mode production.");
      } else {
        toast.error(e?.response?.data?.detail || "Échec de la connexion dev.");
      }
    } finally {
      setLoadingKey(null);
    }
  };

  const resetData = async () => {
    setResetting(true);
    try {
      // Auto-login en CEO pour avoir les droits + tenant
      await api.post("/auth/dev-login", {
        user_key: "cc90af7e-58ff-4d47-8e6c-5b577eedcfa9",
      });
      await api.post("/dev/reset-test-data");
      toast.success("Données de test réinitialisées ✓");
      setConfirmReset(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du reset.");
    } finally {
      setResetting(false);
    }
  };

  const grouped = Object.fromEntries(
    CATEGORY_ORDER.map(([k]) => [k, ACCOUNTS.filter((a) => a.category === k)])
  );

  return (
    <div
      data-testid="dev-switch-user-page"
      className="min-h-screen px-4 sm:px-6 lg:px-8 py-10"
      style={{ background: "#FAFAFA", fontFamily: "'DM Sans', sans-serif" }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <WordmarkInline />
            <span
              className="text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5"
              style={{ background: "#1A1A1A", color: "#F77F00" }}
            >
              <FlaskConical size={11} /> DEV
            </span>
          </div>
          <h1
            className="text-[28px] sm:text-[32px] font-bold leading-tight"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Sélecteur de compte dev
          </h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "#666" }}>
            Choisissez un des 15 membres APOLLO seedés pour une connexion immédiate
            sans Magic Link. Cette page est <strong>désactivée en production</strong>
            : l'endpoint backend renvoie 404 si <code>APP_ENV=prod</code>.
          </p>
          <p className="text-xs mt-2" style={{ color: "#999" }}>
            Les cartes vous indiquent le rôle effectif et le routing modération
            attendu pour chaque membre — utile pour tester les scénarios
            d'approbation rapidement.
          </p>
        </div>

        {/* Groups */}
        {CATEGORY_ORDER.map(([cat, label, help]) => {
          const accounts = grouped[cat];
          if (!accounts.length) return null;
          return (
            <section key={cat} className="mb-8" data-testid={`dev-section-${cat}`}>
              <div className="mb-3">
                <h2
                  className="text-base font-bold uppercase tracking-wider"
                  style={{ fontFamily: "'Sora', sans-serif", color: "#1A1A1A" }}
                >
                  {label}
                </h2>
                <p className="text-xs mt-0.5" style={{ color: "#999" }}>
                  {help}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {accounts.map((a, i) => (
                  <AccountCard
                    key={a.key}
                    account={a}
                    loading={loadingKey === a.key}
                    onClick={() => loginAs(a)}
                    delay={i * 0.04}
                  />
                ))}
              </div>
            </section>
          );
        })}

        <div className="mt-10 p-4 rounded-lg text-sm" style={{
          background: "#FFF7ED", border: "1px dashed #FED7AA", color: "#9A3412",
        }}>
          <strong>Astuce</strong> : ouvrez plusieurs onglets ou utilisez le mode
          incognito pour tester le scénario du <strong>collège Direction</strong>
          (Gilles + Bénédicte en parallèle). Le cookie <code>apollo_bn_session</code>
          est isolé par contexte navigateur.
        </div>

        {/* Reset zone */}
        <div className="mt-10 pt-8 border-t" style={{ borderColor: "#E5E5E5" }}>
          <h2
            className="text-base font-bold uppercase tracking-wider mb-1"
            style={{ fontFamily: "'Sora', sans-serif", color: "#1A1A1A" }}
          >
            Zone dangereuse
          </h2>
          <p className="text-xs mb-4" style={{ color: "#666" }}>
            Vide tous les posts, commentaires, réactions, événements,
            notifications et tokens. <strong>Préserve</strong> tenants, pays,
            régions, groupes, catégories, profils utilisateurs. Aucun impact
            sur les tables legacy APOLLO.
          </p>

          {!confirmReset ? (
            <button
              onClick={() => setConfirmReset(true)}
              data-testid="reset-test-data-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-colors border-2"
              style={{ borderColor: "#EF4444", color: "#EF4444", background: "#FFF" }}
            >
              <Trash2 size={14} /> Reset toutes les données de test
            </button>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-lg flex items-start gap-3 flex-wrap"
              style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}
              data-testid="reset-confirm-modal"
            >
              <AlertTriangle size={20} color="#991B1B" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-[260px]">
                <div className="font-semibold text-sm mb-1" style={{ color: "#7F1D1D" }}>
                  Confirmer le reset complet ?
                </div>
                <div className="text-xs mb-3" style={{ color: "#7F1D1D" }}>
                  Cette action est irréversible. Tous les posts, commentaires,
                  réactions, événements et notifications du tenant <code>apollo</code>
                  seront supprimés. Les comptes utilisateurs et la structure
                  organisationnelle sont préservés.
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={resetData}
                    disabled={resetting}
                    data-testid="reset-confirm-yes"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50"
                    style={{ background: "#EF4444" }}
                  >
                    {resetting && <Loader2 size={14} className="animate-spin" />}
                    {resetting ? "Reset en cours…" : "Oui, tout effacer"}
                  </button>
                  <button
                    onClick={() => setConfirmReset(false)}
                    disabled={resetting}
                    data-testid="reset-confirm-no"
                    className="px-4 py-2 rounded-full text-sm font-semibold border bg-white"
                    style={{ borderColor: "#E5E5E5", color: "#1A1A1A" }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccountCard({ account, loading, onClick, delay }) {
  const Ic = account.icon;
  return (
    <motion.button
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      whileHover={{ y: -2 }}
      onClick={onClick}
      disabled={loading}
      data-testid={`dev-login-${account.key.slice(0, 8)}`}
      className="bg-white rounded-[12px] p-4 text-left transition-shadow hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] disabled:opacity-50 group"
      style={{ border: "1px solid #E5E5E5" }}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${account.color}15` }}
        >
          <Ic size={18} color={account.color} />
        </div>
        <span
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{ background: `${account.color}15`, color: account.color }}
        >
          {account.country}
        </span>
      </div>
      <div className="text-sm font-bold mb-0.5" style={{ color: "#1A1A1A" }}>
        {account.name}
      </div>
      <div className="text-xs mb-1" style={{ color: "#666" }}>
        {account.role} · {account.group}
      </div>
      <div className="text-xs leading-relaxed mb-3" style={{ color: "#999" }}>
        {account.note}
      </div>
      <div
        className="inline-flex items-center gap-1.5 text-xs font-semibold transition-colors group-hover:gap-2"
        style={{ color: account.color }}
      >
        {loading ? (
          <>
            <Loader2 size={12} className="animate-spin" /> Connexion…
          </>
        ) : (
          <>
            Login direct <ArrowRight size={12} />
          </>
        )}
      </div>
    </motion.button>
  );
}
